import {
  AttendanceType,
  AutoInvoiceFrequency,
  AutoInvoicePaymentType,
  ConferenceLinkType,
  ExpenseType,
  RepeatOptions,
  TransactionType,
} from '@prisma/client'
import { literal, z } from 'zod'

export const studentFormSchema = z
  .object({
    firstName: z.string().min(1, { message: 'First name is required.' }),
    lastName: z.string().min(1, { message: 'Last name is required.' }),
    studentEmailAddress: z
      .string()
      .email({ message: 'Invalid email address.' })
      .optional()
      .or(z.literal('')),
    studentPhoneNumber: z.string().optional().or(z.literal('')),
    accountOption: z.enum(['new', 'existing']).optional(),
    groupId: z.string().optional().or(z.literal('')),
    accountId: z.string().optional().or(z.literal('')),
    pricePerLesson: z.coerce
      .number({
        required_error: 'Price per lesson is required',
        invalid_type_error: 'Price per lesson must be a number',
      })
      .nonnegative(),
    lessonLength: z.coerce
      .number({
        required_error: 'Lesson length is required',
        invalid_type_error: 'Lesson length must be a number',
      })
      .nonnegative(),
    primaryContactFirstName: z.string().optional().or(z.literal('')),
    primaryContactLastName: z.string().optional().or(z.literal('')),
    primaryContactEmailAddress: z
      .string()
      .email({ message: 'Invalid email address.' })
      .optional()
      .or(z.literal('')),
    primaryContactPhoneNumber: z.string().optional().or(z.literal('')),
    secondaryContactFirstName: z.string().optional().or(z.literal('')),
    secondaryContactLastName: z.string().optional().or(z.literal('')),
    secondaryContactEmailAddress: z
      .string()
      .email({ message: 'Invalid email address.' })
      .optional()
      .or(z.literal('')),
    secondaryContactPhoneNumber: z.string().optional().or(z.literal('')),
    autoInvoicingEnabled: z.boolean().optional().default(false),
    studentAsPrimaryContact: z.boolean().optional().default(false),
  })
  .refine(
    (data) =>
      data.studentAsPrimaryContact || // Skip validation if student is primary contact
      data.accountOption === 'existing' ||
      data.primaryContactFirstName?.trim() !== '',
    {
      message: 'Primary contact first name is required for new accounts.',
      path: ['primaryContactFirstName'],
    },
  )
  .refine(
    (data) =>
      data.studentAsPrimaryContact || // Skip validation if student is primary contact
      data.accountOption === 'existing' ||
      data.primaryContactLastName?.trim() !== '',
    {
      message: 'Primary contact last name is required for new accounts.',
      path: ['primaryContactLastName'],
    },
  )
export const editStudentFormSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required.' }),
  lastName: z.string().min(1, { message: 'Last name is required.' }),
  studentEmailAddress: z
    .string()
    .email({ message: 'Invalid email address.' })
    .optional()
    .or(z.literal('')),
  studentPhoneNumber: z.string().optional().or(z.literal('')),
  pricePerLesson: z.coerce
    .number({
      required_error: 'Price per lesson is required',
      invalid_type_error: 'Price per lesson must be a number',
    })
    .nonnegative(),
  lessonLength: z.coerce
    .number({
      required_error: 'Lesson length is required',
      invalid_type_error: 'Lesson length must be a number',
    })
    .nonnegative(),
})

export const editAccountFormSchema = z.object({
  primaryContactFirstName: z.string().optional().or(z.literal('')),
  primaryContactLastName: z.string().optional().or(z.literal('')),
  primaryContactEmailAddress: z
    .string()
    .email({ message: 'Invalid email address.' })
    .optional()
    .or(z.literal('')),
  primaryContactPhoneNumber: z.string().optional().or(z.literal('')),
  secondaryContactFirstName: z.string().optional().or(z.literal('')),
  secondaryContactLastName: z.string().optional().or(z.literal('')),
  secondaryContactEmailAddress: z
    .string()
    .email({ message: 'Invalid email address.' })
    .optional()
    .or(z.literal('')),
  secondaryContactPhoneNumber: z.string().optional().or(z.literal('')),
  autoInvoicingEnabled: z.boolean().optional().default(false),
})

export const lessonFormSchema = z
  .object({
    studentId: z.string().min(1, { message: 'Student is required.' }),
    lessonDate: z.date({
      required_error: 'Date is required',
      invalid_type_error: 'Invalid date format',
    }),
    calendarTitle: z
      .string()
      .max(250, { message: 'Title must not exceed 250 characters.' })
      .optional(),
    calendarGuests: z
      .array(
        z
          .string()
          .email({ message: 'Each guest must be a valid email address.' }),
      )
      .optional()
      .default([]),
    calendarReminder: z.boolean().optional().default(true),
    calendarReminderMinutesBefore: z.coerce
      .number({
        required_error: 'Lesson price is required',
        invalid_type_error: 'Lesson price must be a number',
      })
      .nonnegative()
      .max(40320, {
        message: 'Minutes before reminder must not exceed 40320 (28 days).',
      })
      .optional(),
    lessonEndDate: z
      .date({
        invalid_type_error: 'Invalid date format',
      })
      .optional(), // Initially optional, conditionally required later
    lessonStartTime: z
      .string()
      .min(1, { message: 'Lesson start time is required.' }),
    lessonEndTime: z
      .string()
      .min(1, { message: 'Lesson end time is required.' }),
    frequency: z.nativeEnum(RepeatOptions),
    attendance: z.nativeEnum(AttendanceType),
    lessonConferenceLinkType: z
      .nativeEnum(ConferenceLinkType)
      .optional()
      .or(literal(undefined)),
    lessonPrice: z.coerce
      .number({
        required_error: 'Lesson price is required',
        invalid_type_error: 'Lesson price must be a number',
      })
      .nonnegative(),
    updateSeries: z.boolean().optional(),
    lessonDescription: z.string().max(250).optional().or(z.literal('')),
  })
  .refine(
    (data) => {
      // Enforce `lessonEndDate` when `frequency` is not 'does not repeat'
      return (
        data.frequency === RepeatOptions.DOES_NOT_REPEAT ||
        data.lessonEndDate !== undefined
      )
    },
    {
      message: 'End date is required when lesson repeats',
      path: ['lessonEndDate'], // Point the error to `lessonEndDate`
    },
  )
  .refine(
    (data) => {
      // Ensure `lessonEndTime` is after `lessonStartTime`
      const startTime = new Date(`1970-01-01T${data.lessonStartTime}:00`)
      const endTime = new Date(`1970-01-01T${data.lessonEndTime}:00`)
      return endTime > startTime
    },
    {
      message: 'End time must be after start time',
      path: ['lessonEndTime'],
    },
  )

export const eventFormSchema = z
  .object({
    title: z.string().min(1, { message: 'Title is required.' }),
    amount: z.coerce
      .number({
        required_error: 'Amount is required',
        invalid_type_error: 'Amount must be a number',
      })
      .nonnegative(),
    paid: z.boolean().optional().default(false), // Default to false if not explicitly provided
    eventDate: z.date({
      required_error: 'Date is required',
      invalid_type_error: 'Invalid date format',
    }),
    eventColor: z.string().optional().or(z.literal('')),
    eventStartTime: z
      .string()
      .min(1, { message: 'Event start time is required.' }),
    eventEndTime: z.string().min(1, { message: 'Event end time is required.' }),
  })
  .refine(
    (data) => {
      // Ensure `lessonEndTime` is after `lessonStartTime`
      const startTime = new Date(`1970-01-01T${data.eventStartTime}:00`)
      const endTime = new Date(`1970-01-01T${data.eventEndTime}:00`)
      return endTime > startTime
    },
    {
      message: 'End time must be after start time',
      path: ['lessonEndTime'],
    },
  )

export const transactionFormSchema = z.object({
  accountId: z.string().min(1, { message: 'Account is required' }),
  transactionDate: z.date({
    required_error: 'Transaction date is required',
    invalid_type_error: 'Invalid date format',
  }),
  sendEmail: z.boolean().optional(),
  cc: z.boolean().optional(),
  emailRecipients: z
    .array(
      z
        .string()
        .email({ message: 'Each recipient must be a valid email address.' }),
    )
    .optional()
    .default([]),
  type: z.preprocess(
    (val) => (val === '' ? undefined : val), // Convert empty string to undefined
    z.nativeEnum(TransactionType, {
      required_error: 'Transaction type is required',
      invalid_type_error: 'Invalid transaction type',
    }),
  ),
  description: z.string().max(250).optional().or(z.literal('')),
  amount: z.coerce
    .number({
      required_error: 'Lesson price is required',
      invalid_type_error: 'Lesson price must be a number',
    })
    .positive({ message: 'Transaction amount must be greater than $0' }),
})

export const expenseFormSchema = z
  .object({
    transactionDate: z.date({
      required_error: 'Transaction date is required',
      invalid_type_error: 'Invalid date format',
    }),
    expenseType: z.nativeEnum(ExpenseType, {
      required_error: 'Expense type is required',
      invalid_type_error: 'Invalid expense type',
    }),
    destination: z.string().optional().or(z.literal('')),
    distance: z.coerce
      .number()
      .min(0, { message: 'Distance amount must be 0 or greater' }) // Allows 0 as a valid value
      .optional(),
    vehicle: z.string().optional().or(z.literal('')),
    description: z.string().max(250).optional().or(z.literal('')),
    amount: z.coerce
      .number({
        required_error: 'Amount is required',
        invalid_type_error: 'Amount must be a number',
      })
      .optional(),
  })
  .refine(
    (data) =>
      data.expenseType === ExpenseType.MILEAGE ||
      data.amount == null ||
      data.amount > 0,
    {
      message: 'Amount must be greater than $0 for non-mileage expenses',
      path: ['amount'],
    },
  )
  .refine(
    (data) =>
      data.expenseType !== ExpenseType.MILEAGE ||
      (data.destination && data.destination.trim() !== ''),
    {
      message: 'Destination is required',
      path: ['destination'],
    },
  )
  .refine(
    (data) => data.expenseType !== ExpenseType.MILEAGE || data.distance != null,
    {
      message: 'Distance is required',
      path: ['distance'],
    },
  )
  .refine(
    (data) =>
      data.expenseType !== ExpenseType.MILEAGE ||
      (data.vehicle && data.vehicle.trim() !== ''),
    {
      message: 'Vehicle is required',
      path: ['vehicle'],
    },
  )
export const invoiceFormSchema = z.object({
  accountIds: z.array(z.string()),
  invoiceDate: z.date({
    required_error: 'Invoice date is required',
    invalid_type_error: 'Invalid date format',
  }),
  invoiceDueDate: z.date({
    required_error: 'Invoice due date is required',
    invalid_type_error: 'Invalid date format',
  }),
  date: z.object({
    from: z.date(),
    to: z.date(),
  }),
  range: z.string().optional().or(z.literal('')),
  includePreviousBalances: z.boolean().optional(),
})

export const groupFormSchema = z.object({
  groupName: z.string().max(100).min(1, { message: 'Group Name is required' }),
  groupNotes: z.string().max(100),
  groupColor: z.string().optional().or(z.literal('')),
  studentIds: z.array(z.string()),
})

export const notesFormSchema = z.object({
  studentId: z.string().min(1, { message: 'Student is required.' }),
  lessonId: z.string().optional().or(z.literal('')),
  noteId: z.string().min(1, { message: 'Account is required' }),
  note: z.union([z.string().max(1000), z.literal(''), z.undefined()]),
})

export const attendanceSchema = z.object({
  attendanceRecord: z.array(
    z.object({
      lessonId: z.string({
        required_error: 'Lesson ID is required',
      }),
      attendance: z.nativeEnum(AttendanceType, {
        required_error: 'Attendance type is required',
        invalid_type_error: 'Invalid attendance type',
      }),
    }),
  ),
})

export const contactFormSchema = z.object({
  name: z.string().min(1, { message: 'Name is required.' }),

  emailAddress: z.string().email({ message: 'Invalid email address.' }),

  message: z
    .string()
    .min(1, { message: 'Message is required' })
    .max(1000, { message: 'Message is too long.' }),
})

export const firstTimeLoginFormSchema = z.object({
  name: z.string().min(1, { message: 'Name is required.' }),
  timezone: z.string().min(1, { message: 'Timezone is required.' }),
})

export const transactionSearchFormSChema = z.object({
  type: z.preprocess(
    (val) => (val === '' ? undefined : val), // Convert empty string to undefined
    z
      .nativeEnum(TransactionType, {
        required_error: 'Transaction type is required',
        invalid_type_error: 'Invalid transaction type',
      })
      .optional()
      .or(z.literal('')),
  ),
  date: z.object({
    from: z.date(),
    to: z.date(),
  }),
  range: z.string().optional().or(z.literal('')),
  studentId: z.string().optional().or(z.literal('')),
})

export const autoInvoicingFormSchema = z.object({
  autoInvoice: z.boolean().optional(),
  autoInvoiceBillingCycleStartDate: z.date({
    required_error: 'Invoice date is required',
    invalid_type_error: 'Invalid date format',
  }), // Allows null since it's optional
  autoInvoiceStartTime: z.string().min(1, { message: 'Name is required.' }),
  autoInvoiceFrequency: z.nativeEnum(AutoInvoiceFrequency).optional(),
  autoInvoicePaymentType: z.nativeEnum(AutoInvoicePaymentType).optional(),
  autoInvoiceDaysAfterDueDate: z.coerce
    .number({
      required_error: 'Days after due is required',
      invalid_type_error: 'Days after due must be a number',
    })
    .nonnegative(), // Use z.number().nullable() for Decimal?
  autoInvoiceBalanceForward: z.boolean().optional(),
  // autoInvoiceTimePeriod: z.nativeEnum(InvoicePeriod, {
  //     required_error: "Invoice period is required",
  //     invalid_type_error: "Invalid invoice period",
  // })
})

export const settingsProfileFormSchema = z.object({
  name: z.string().min(1, { message: 'Name is required.' }),
  timezone: z.string().min(1, { message: 'Timezone is required.' }),
})

export const emailTemplateFormSchema = z.object({
  subject: z.string().min(1, { message: 'Subject is required.' }),
  body: z.string().min(1, { message: 'Email body is required.' }),
})

export const settingsInvoiceFormSchema = z.object({
  venmo: z.string().optional().or(z.literal('')),
  zelle: z.string().optional().or(z.literal('')),
  paypal: z.string().optional().or(z.literal('')),
})

export const settingsCalendarFormSchema = z.object({
  showWeekends: z.boolean().optional(),
  startTime: z.string().optional().or(z.literal('')),
  endTime: z.string().optional().or(z.literal('')),
})

export const demoFormSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, { message: 'First name too long' }),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, { message: 'Last name too long' }),
  email: z.string().email('Invalid email address').max(100),

  problemToSolve: z.enum([
    'Invoicing',
    'Payment tracking',
    'Student organization',
    'Other',
  ]),
})
